/**
 * BÁO CÁO CUỘC HỌP TUẦN của một VĂN PHÒNG (thêm 23/09/2026).
 *
 * Mỗi văn phòng mỗi tuần MỘT bản: chủ trì, người dự, nội dung chính, tồn đọng, việc giao cho ai
 * làm tới bao giờ, và kế hoạch tuần tới. Bản ghi `meeting` mang mã văn phòng (`vp`) nên phòng khác
 * không đọc được biên bản của nhau; Giám đốc đọc được của mọi phòng.
 *
 * Tuần khoá theo THỨ HAI của tuần ấy, dạng «DD/MM/YYYY» — cùng một khoá với KPI tuần và bảng thị
 * trường, để sau này ghép số liệu với biên bản không phải đổi cách tính tuần.
 */
import { OFFICE_KEY, type RecordDto } from '@halo/shared';
import { compactData, readString, type RecordData } from './parsing';

/** Một việc được giao trong cuộc họp. */
export interface MeetingAction {
  text: string;
  /** Tên đăng nhập người nhận việc; '' = chưa giao ai. */
  assignee: string;
  /** «DD/MM/YYYY»; '' = chưa hẹn hạn. */
  due: string;
  done: boolean;
}

export interface MeetingReport {
  recordId: number;
  office: string;
  /** Chủ nhật mở đầu tuần, «DD/MM/YYYY». */
  weekKey: string;
  host: string;
  attendees: string;
  summary: string;
  issues: string;
  next: string;
  actions: MeetingAction[];
  /** Tên đăng nhập người lưu lần cuối. */
  updatedBy: string;
  /** ISO-8601; '' = chưa lưu lần nào. */
  updatedAt: string;
}

const readActions = (value: unknown): MeetingAction[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const row = item as Record<string, unknown>;
    const text = typeof row.t === 'string' ? row.t : '';
    const assignee = typeof row.w === 'string' ? row.w : '';
    const due = typeof row.d === 'string' ? row.d : '';
    return text.trim() || assignee || due ? [{ text, assignee, due, done: row.k === true }] : [];
  });
};

export function parseMeetingReport(record: RecordDto): MeetingReport {
  const data = record.data;
  return {
    recordId: record.id,
    office: readString(data, OFFICE_KEY).trim().toLowerCase(),
    weekKey: readString(data, 'w'),
    host: readString(data, 'host'),
    attendees: readString(data, 'att'),
    summary: readString(data, 'sum'),
    issues: readString(data, 'issue'),
    next: readString(data, 'next'),
    actions: readActions(data.act),
    updatedBy: readString(data, 'by'),
    updatedAt: readString(data, 'at'),
  };
}

export function parseMeetingReports(records: readonly RecordDto[]): MeetingReport[] {
  return records.map(parseMeetingReport);
}

/** Bỏ việc rỗng (không chữ, không người, không hạn) trước khi ghi xuống. */
export function normalizeActions(actions: readonly MeetingAction[]): MeetingAction[] {
  return actions
    .map((action) => ({ ...action, text: action.text.trim() }))
    .filter((action) => action.text || action.assignee || action.due);
}

/** Ô rỗng thì KHÔNG ghi khoá — bản ghi chỉ giữ những gì có thật. */
export function serializeMeetingReport(
  report: Omit<MeetingReport, 'recordId'>,
  now: string,
  updatedBy: string,
): RecordData {
  const actions = normalizeActions(report.actions);
  return compactData({
    [OFFICE_KEY]: report.office,
    w: report.weekKey,
    host: report.host.trim() || undefined,
    att: report.attendees.trim() || undefined,
    sum: report.summary.trim() || undefined,
    issue: report.issues.trim() || undefined,
    next: report.next.trim() || undefined,
    act: actions.length
      ? actions.map((action) => ({
          t: action.text,
          w: action.assignee || undefined,
          d: action.due || undefined,
          k: action.done || undefined,
        }))
      : undefined,
    by: updatedBy,
    at: now,
  });
}

export function newMeetingReport(office: string, weekKey: string): MeetingReport {
  return {
    recordId: 0,
    office,
    weekKey,
    host: '',
    attendees: '',
    summary: '',
    issues: '',
    next: '',
    actions: [],
    updatedBy: '',
    updatedAt: '',
  };
}

export const meetingKey = (office: string, weekKey: string) => `${office}|${weekKey}`;

/**
 * Một văn phòng một tuần một biên bản: trùng thì bản ghi MỚI HƠN (id lớn hơn) thắng — hai người
 * cùng bấm lưu lần đầu thì bản sau là bản người ta đọc, bản trước nằm im chứ không trộn lẫn.
 */
export function latestMeetings(reports: readonly MeetingReport[]): Map<string, MeetingReport> {
  const index = new Map<string, MeetingReport>();
  for (const report of reports) {
    const key = meetingKey(report.office, report.weekKey);
    const current = index.get(key);
    if (!current || current.recordId < report.recordId) index.set(key, report);
  }
  return index;
}

/** Lý do chưa lưu được, hoặc null: biên bản trống thì không lưu. */
export function meetingProblem(report: Omit<MeetingReport, 'recordId'>): string | null {
  if (!report.office) return 'Chưa biết biên bản này của văn phòng nào';
  if (!report.weekKey) return 'Chưa biết biên bản này của tuần nào';
  const hasText = [report.host, report.attendees, report.summary, report.issues, report.next].some(
    (value) => value.trim(),
  );
  if (hasText || normalizeActions(report.actions).length) return null;
  return 'Biên bản trống — ghi ít nhất nội dung chính hoặc một việc cần làm';
}

/** «3/7 việc xong». Không có việc nào thì `total` = 0. */
export function actionProgress(actions: readonly MeetingAction[]) {
  const wanted = normalizeActions(actions);
  return { done: wanted.filter((action) => action.done).length, total: wanted.length };
}

/** Việc CHƯA XONG của các biên bản, cũ nhất trước — dùng để nhắc lại ở tuần sau. */
export function openActions(
  reports: readonly MeetingReport[],
): Array<MeetingAction & { weekKey: string }> {
  return reports
    .flatMap((report) =>
      normalizeActions(report.actions)
        .filter((action) => !action.done)
        .map((action) => ({ ...action, weekKey: report.weekKey })),
    )
    .reverse();
}
